import { apiPostAsync } from '../common/service-utils'
import { USER_GET_TFA_DETAILS_DATA, USER_CONTACT_VERIFIED, TFA_UPDATE_STATUS, TFA_SET_ERROR, TFA_CLEAR_ERROR } from './actionTypes';

export * from './actionTypes';
import * as constants from '../common/constants'
import * as errorCodes from '../common/errorCodes'
import * as JspUtil from '../common/JspUtil'
import { hashHistory } from '../common/navigationService'
import * as loginactions from '../loginpages/actionCreators'
import {
    create,
    parseCreationOptionsFromJSON,
    parseRequestOptionsFromJSON,
    get
} from "@github/webauthn-json/browser-ponyfill"
import { TFA_STATE, WEBAUTHN_DETAILS } from "../loginpages/actionCreators";

// --- Action Types ---

// WebAuthn status enum
export const WebAuthnStatus = Object.freeze({
    IDLE: '',
    PROCESSING: 'Processing',
    SUCCESS: 'Success',
    FAILED: 'Failed',
});

// --- WebAuthn error message helper ---

const WEBAUTHN_ERROR_MESSAGES = {
    [errorCodes.WEBAUTHN_USER_CANCELLED]: {
        registration: () => JspUtil.getLabelFromJspVariables('err_user_cancelled_registration'),
        authentication: () => JspUtil.getLabelFromJspVariables('err_user_cancelled_authentication'),
    },
    [errorCodes.WEBAUTHN_DEVICE_ALREADY_REGISTERED]: {
        registration: () => JspUtil.getLabelFromJspVariables('err_device_already_registered'),
        authentication: () => JspUtil.getLabelFromJspVariables('err_device_already_registered'),
    },
};

function getWebAuthnErrorMessage(errorCode, context) {
    const getter = WEBAUTHN_ERROR_MESSAGES[errorCode]?.[context];
    return getter ? getter() : JspUtil.getLabelFromJspVariables('err_unknown');
}

// --- Helper for companyIds ---

function getCompanyIdData() {
    if (typeof jspCompanyIds !== 'undefined') {
        const companyIds = (jspCompanyIds || []).join(",");
        if (companyIds !== '') return { companyIds };
    }
    return {};
}

// --- WebAuthn credential helpers ---

async function createCredential(json) {
    const publicKey = JSON.parse(json)
    const options = parseCreationOptionsFromJSON(publicKey);
    const response = await create(options);
    return JSON.stringify(response);
}

export async function authCredential(json) {
    const publicKey = JSON.parse(json)
    const options = parseRequestOptionsFromJSON(publicKey);
    const response = await get(options);
    return JSON.stringify(response);
}

// --- Action Creators ---

// Plain async versions — component handles response and errors
export async function getWebAuthnRegistrationsAsync(appKey, userId) {
    return apiPostAsync(constants.API_GET_WEBAUTHN_REGISTRATIONS, null, { userName: userId, appKey });
}

export async function loadAuthenticatorDevicesAsync(userName, appKey) {
    return apiPostAsync(constants.API_GET_AUTHENTICATOR_DEVICES, null, { userName, appKey });
}

export async function getTfaDetailsAuthenticatedAsync(skipCheckIfSessionIs2FAed = false) {
    return apiPostAsync(constants.API_GET_TFA_DETAILS_AUTHENTICATED, null, { skipCheckIfSessionIs2FAed });
}

export async function deleteWebAuthnRegistrationAsync(appKey, userId, reg) {
    return apiPostAsync(constants.API_DELETE_WEBAUTHN_REGISTRATION, null, {
        userName: userId, appKey, deviceNumber: '' + reg.deviceNumber, deviceName: reg.deviceName
    });
}

export async function deleteDeviceAsync(appKey, userId, deviceName) {
    return apiPostAsync(constants.API_DELETE_AUTHENTICATOR_DEVICE, null, { userName: userId, appKey, deviceName });
}

export async function sendEmailVerificationCodeAsync(tfaEmail) {
    return apiPostAsync(constants.API_EMAIL_VERIFICATION_CODE_AUTHENTICATED, null, { email: tfaEmail });
}

export async function emailVerificationCodeAsync(userDetail) {
    return apiPostAsync(constants.API_EMAIL_VERIFICATION_CODE, null, {
        userId: userDetail.userName,
        password: userDetail.password,
        appKey: userDetail.appKey,
        email: userDetail.tfaEmail,
        authenicated: userDetail.authenticated,
        Authenicated: userDetail.authenticated,
        ...getCompanyIdData()
    });
}

export async function verifyEmailVerificationCodeAsync(userDetail) {
    return apiPostAsync(constants.API_VERIFY_VERIFICATION_CODE, null, {
        userId: userDetail.userName,
        password: userDetail.password,
        appKey: userDetail.appKey,
        email: userDetail.tfaEmail,
        verificationCode: userDetail.verificationCode,
        authenicated: userDetail.authenticated,
        Authenicated: userDetail.authenticated,
        ...getCompanyIdData()
    });
}

export function confirmGlobalId(email, firstName, lastName, newPassword, newConfirmPassword, otpCode) {
    return async function (dispatch) {
        dispatch({ type: TFA_CLEAR_ERROR });

        if (!JspUtil.isValueAvailable(otpCode)) {
            dispatch({ type: TFA_SET_ERROR, data: { message: JspUtil.getLabelFromJspVariables('alert_confirmation_code_required') } });
            return;
        }
        if (!JspUtil.isValueAvailable(newPassword)) {
            dispatch({ type: TFA_SET_ERROR, data: { message: JspUtil.getLabelFromJspVariables('alert_password_required') } });
            return;
        }
        if (newPassword != newConfirmPassword) {
            dispatch({ type: TFA_SET_ERROR, data: { message: JspUtil.getLabelFromJspVariables('alert_passwords_dont_match') } });
            return;
        }

        try {
            const resp = await apiPostAsync(constants.API_CONFIRM_GLOBAL_ID, null, {
                globalId: email,
                firstName,
                lastName,
                newPassword,
                tfaPasscode: otpCode
            });
            const respData = {
                tfaEmail: email,
                app: constants.APP_KEY_GLOBAL,
                isActiveTfaEmail: constants.YES
            };
            dispatch({ type: USER_GET_TFA_DETAILS_DATA, data: respData });
            hashHistory.push({
                pathname: constants.ROUTE_PAGE_TFA_REGISTRATION_DONE,
                state: {
                    tfaEmail: email,
                    appKey: constants.APP_KEY_GLOBAL,
                    isActiveTfaEmail: constants.YES
                }
            });
        } catch (resp) {
            if (resp.code == errorCodes.ERR_PASSWORD_EXPIRED) {
                hashHistory.push({
                    pathname: constants.ROUTE_PAGE_MESSAGE,
                    state: {
                        msg: JspUtil.getLabelFromJspVariables('confirmation_code_expired_sent_email', email),
                        heading: null,
                        redirectUrl: null
                    }
                });
            } else if (resp.code == errorCodes.ERR_INVALID_OLD_PASSWORD) {
                dispatch({ type: TFA_SET_ERROR, data: { message: JspUtil.getLabelFromJspVariables('err_globalid_already_registered'), code: resp.code } });
            } else {
                dispatch({ type: TFA_SET_ERROR, data: { message: resp.message, code: resp.code } });
            }
        }
    }
}

export function getTfaDetails(isUserAlreadyAuthenticated, userId, appKey, password) {
    return async function (dispatch) {
        dispatch({ type: TFA_CLEAR_ERROR });
        try {
            const resp = await apiPostAsync(constants.API_GET_TFA_DETAILS, null, {
                userId,
                password,
                appKey,
                ...getCompanyIdData()
            });
            dispatch({ type: USER_GET_TFA_DETAILS_DATA, data: resp.data });
            // TODO: previously called `dispatch(getTfaDetailsAuthenticated(isUserAlreadyAuthenticated))`
            // — function never existed (only the async variant does), so the call always threw and
            // was swallowed by the catch. Removed to stop falsely flagging getTfaDetails as failed.
            // If supplementary authenticated TFA details are needed, call getTfaDetailsAuthenticatedAsync
            // and merge its response into USER_GET_TFA_DETAILS_DATA.
        } catch (resp) {
            // Server returns partial data even on some error codes
            if (resp.data) dispatch({ type: USER_GET_TFA_DETAILS_DATA, data: resp.data });
            if (Number(resp.code) === errorCodes.ERR_TFA_MISSING_EMAIL_VERIFY) {
                hashHistory.push(constants.ROUTE_PAGE_TFA_EMAIL_REGISTER);
            } else {
                dispatch({ type: TFA_SET_ERROR, data: { message: resp.message, code: resp.code } });
            }
        }
    }
}


/**
 * Pre-checks 2FA session status before navigating to TFA settings.
 * Called from dashboard UserMenu — user stays on current page while API check runs.
 *
 * - Session is 2FA verified → navigate to PageTfaRegister
 * - Session not 2FA verified (-32320) → navigate to PageUpdateTfaLogin (user sends OTP via API)
 * - Not authenticated → navigate to PageUpdateTfaLogin
 */
export function checkAndNavigateToTfaSettings() {
    return async function (dispatch, getState) {
        const isAuthenticated = getState().authenticateReducer.isAuthenticated;

        if (!isAuthenticated) {
            hashHistory.push(constants.ROUTE_PAGE_UPDATE_TFA_LOGIN);
            return;
        }

        try {
            const resp = await apiPostAsync(constants.API_GET_TFA_DETAILS_AUTHENTICATED, null, {
                skipCheckIfSessionIs2FAed: false
            });
            dispatch({ type: USER_GET_TFA_DETAILS_DATA, data: resp.data });
            hashHistory.push(constants.ROUTE_PAGE_TFA_REGISTER);
        } catch (resp) {
            hashHistory.push(constants.ROUTE_PAGE_UPDATE_TFA_LOGIN);
        }
    }
}

export async function verifyNSaveTfaDetailsAsync(isUserAlreadyAuthenticated, password, userTfaDetails) {
    const postData = {
        '_userName': userTfaDetails.app,
        '_password': password,
        '_app': userTfaDetails.app,
        '_tfaEmail': userTfaDetails.tfaEmail,
        '_isActiveTfaEmail': userTfaDetails.isActiveTfaEmail,
        '_isActiveTfaMobile': userTfaDetails.isActiveTfaMobile,
        'isActiveTfaWebAuthn': userTfaDetails.isActiveTfaWebAuthn
    };

    let apiName = constants.API_VERIFY_N_SAVE_TFA_DETAILS_AUTHENTICATED;
    if (!isUserAlreadyAuthenticated) {
        postData['_password'] = userTfaDetails.password;
        apiName = constants.API_VERIFY_N_SAVE_TFA_DETAILS;
    }

    if (userTfaDetails.firstName && userTfaDetails.lastName) {
        postData['firstName'] = userTfaDetails.firstName.trim();
        postData['lastName'] = userTfaDetails.lastName.trim();
    }

    return apiPostAsync(apiName, null, postData);
}

// Legacy thunk version — used by emailVerificationCode, saveTfaDetailsDuringRegistration, globalidpages
export function verifyNSaveTfaDetails(isUserAlreadyAuthenticated, password, userTfaDetails) {
    return async function (dispatch) {
        dispatch({ type: TFA_CLEAR_ERROR });
        try {
            const resp = await verifyNSaveTfaDetailsAsync(isUserAlreadyAuthenticated, password, userTfaDetails);
            dispatch({ type: USER_GET_TFA_DETAILS_DATA, data: resp.data });
            dispatch({ type: USER_CONTACT_VERIFIED, data: true });
            hashHistory.push({
                pathname: constants.ROUTE_PAGE_TFA_REGISTRATION_DONE,
                state: { action: 'TFA_REGISTERED' }
            });
        } catch (resp) {
            dispatch({ type: TFA_SET_ERROR, data: { message: resp.message, code: resp.code } });
        }
    }
}

export function getWebAuthnStartRegistrationRequest(appKey, userName, deviceName) {
    return async function (dispatch) {
        dispatch({ type: TFA_CLEAR_ERROR });
        dispatch({ type: TFA_UPDATE_STATUS, data: { webAuthnRegistration: WebAuthnStatus.PROCESSING } });
        try {
            const resp = await apiPostAsync(constants.API_WEBAUTHN_STARTREGISTRATION, null, {
                deviceName,
                userName,
                appKey
            });
            dispatch(createWebAuthnCredential(appKey, userName, deviceName, resp.data));
        } catch (resp) {
            dispatch({ type: TFA_UPDATE_STATUS, data: { webAuthnRegistration: WebAuthnStatus.FAILED } });
            dispatch({ type: TFA_SET_ERROR, data: { message: resp.message, code: resp.code } });
        }
    }
}

export function getWebAuthnAuthRequest(appKey, userName) {
    return async function (dispatch) {
        dispatch({ type: TFA_CLEAR_ERROR });
        dispatch({ type: TFA_UPDATE_STATUS, data: { webAuthnAuth: WebAuthnStatus.PROCESSING } });
        try {
            const resp = await apiPostAsync(constants.API_WEBAUTHN_STARTASSERTION, null, {
                userName,
                appKey
            });
            dispatch(authWebAuthn(appKey, userName, resp.data));
        } catch (resp) {
            dispatch({ type: TFA_UPDATE_STATUS, data: { webAuthnAuth: WebAuthnStatus.FAILED } });
            dispatch({ type: TFA_SET_ERROR, data: { message: resp.message, code: resp.code } });
        }
    }
}

export function createWebAuthnCredential(appKey, userName, deviceName, data) {
    return async function (dispatch) {
        const options = data.credentialCreateJson;
        const requestId = data.requestId;
        try {
            const val = await createCredential(options);
            const resp = await apiPostAsync(constants.API_WEBAUTHN_FINISHREGISTRATION, null, {
                userName,
                appKey,
                deviceName,
                requestId,
                publicKey: val
            });
            dispatch({ type: TFA_UPDATE_STATUS, data: { webAuthnRegistration: WebAuthnStatus.SUCCESS } });
        } catch (error) {
            // WebAuthn browser errors have error.code, server errors have error.code too
            const isWebAuthnError = error instanceof DOMException || error.name === 'NotAllowedError';
            if (isWebAuthnError) {
                const message = getWebAuthnErrorMessage(error.code, 'registration');
                dispatch({ type: TFA_SET_ERROR, data: { message, code: error.code } });
            } else {
                dispatch({ type: TFA_SET_ERROR, data: { message: error.message, code: error.code } });
            }
            dispatch({ type: TFA_UPDATE_STATUS, data: { webAuthnRegistration: WebAuthnStatus.FAILED } });
        }
    }
}

export function authWebAuthn(appKey, userName, data) {
    return async function (dispatch) {
        const options = data.credentialCreateJson;
        const requestId = data.requestId;
        try {
            const val = await authCredential(options);
            await apiPostAsync(constants.API_WEBAUTHN_FINISHASSERTION, null, {
                userName,
                appKey,
                requestId,
                publicKey: val
            });
            dispatch({ type: TFA_UPDATE_STATUS, data: { webAuthnAuth: WebAuthnStatus.SUCCESS } });
        } catch (error) {
            const isWebAuthnError = error instanceof DOMException || error.name === 'NotAllowedError';
            if (isWebAuthnError) {
                const message = getWebAuthnErrorMessage(error.code, 'authentication');
                dispatch({ type: TFA_SET_ERROR, data: { message, code: error.code } });
            } else {
                dispatch({ type: TFA_SET_ERROR, data: { message: error.message, code: error.code } });
            }
            dispatch({ type: TFA_UPDATE_STATUS, data: { webAuthnAuth: WebAuthnStatus.FAILED } });
        }
    }
}

export function loginWebAuthn(authParam, data) {
    return async function (dispatch) {
        dispatch({ type: TFA_CLEAR_ERROR });
        const options = data.credentialCreateJson;
        const requestId = data.requestId;
        const appKey = data.app;
        const userName = data.user;
        try {
            const val = await authCredential(options);
            const webAuthn = { userName, appKey, requestId, publicKey: val };
            if (authParam.appUserLoginTfa === 'True') {
                dispatch({ type: WEBAUTHN_DETAILS, data: webAuthn });
            } else {
                dispatch(loginactions.authenticateUser(
                    authParam.userName,
                    authParam.appKey,
                    authParam.loginApp,
                    authParam.password,
                    authParam.passwordLessOtp,
                    authParam.captchaResponse,
                    'webAuthn',
                    authParam.mersOrganization,
                    authParam.rememberMe,
                    webAuthn,
                    '',
                    authParam.appUserLoginTfa
                ));
            }
        } catch (error) {
            const message = getWebAuthnErrorMessage(error.code, 'authentication');
            dispatch({ type: TFA_SET_ERROR, data: { message, code: error.code } });
            dispatch({ type: TFA_STATE, data: { webAuthnRequest: '', useOverride: '' } });
            dispatch({ type: TFA_UPDATE_STATUS, data: { webAuthnAuth: WebAuthnStatus.FAILED } });
        }
    }
}

